import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle } from "lucide-react";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadInfo, setLeadInfo] = useState<any>(null);

  const enrollmentId = searchParams.get("enrollment_id");
  const token = searchParams.get("token");

  useEffect(() => {
    if (enrollmentId) {
      loadEnrollmentInfo();
    }
  }, [enrollmentId]);

  const loadEnrollmentInfo = async () => {
    if (!enrollmentId) return;

    try {
      const { data, error } = await supabase
        .from("campaign_enrollments")
        .select(`
          *,
          lead:leads(contact_name, email),
          campaign:ai_campaigns(name)
        `)
        .eq("id", enrollmentId)
        .single();

      if (error) throw error;
      setLeadInfo(data);
    } catch (err: any) {
      console.error("Error loading enrollment:", err);
    }
  };

  const handleUnsubscribe = async () => {
    if (!enrollmentId || !token) {
      setError("Invalid unsubscribe link");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from("campaign_enrollments")
        .update({
          status: "unsubscribed",
          unsubscribed_at: new Date().toISOString(),
        })
        .eq("id", enrollmentId);

      if (error) throw error;

      setSuccess(true);
    } catch (err: any) {
      console.error("Error unsubscribing:", err);
      setError("Failed to unsubscribe. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  if (!enrollmentId || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Invalid Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              This unsubscribe link is invalid or has expired. Please contact support if you continue to receive emails.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Successfully Unsubscribed
            </CardTitle>
            <CardDescription>
              You have been removed from this email campaign
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You will no longer receive emails from this campaign. If you change your mind, please contact us to re-enroll.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Unsubscribe from Email Campaign</CardTitle>
          <CardDescription>
            {leadInfo ? (
              <>
                <span className="font-medium">{leadInfo.lead?.contact_name}</span>
                {" - "}
                <span className="text-xs">{leadInfo.lead?.email}</span>
                <br />
                Campaign: <span className="font-medium">{leadInfo.campaign?.name}</span>
              </>
            ) : (
              "We're sorry to see you go"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to unsubscribe from this email campaign. You will no longer receive any emails from this sequence.
          </p>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            onClick={handleUnsubscribe}
            disabled={loading}
            variant="destructive"
            className="w-full"
          >
            {loading ? "Unsubscribing..." : "Confirm Unsubscribe"}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            If you believe you received this in error, please contact our support team.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
